import { FC } from "react";
import { Formik, FormikHelpers } from "formik";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { ReportMediaObjectFieldsFragment, useCreateReportDeliverableFileMutation } from "app/api/graph/types";
import { reportVersionGraphId } from "app/api/graph/helpers";
import yup from "app/util/yup.ts";
import { StatusAlert } from "app/component/form/StatusAlert.tsx";
import { RichTextEditor } from "app/component/form/text-editor/RichTextEditor.tsx";
import { handleError } from "app/api/graph/form.ts";
import { MediaObjects } from "app/component/form/media-objects/MediaObjects.tsx";

type Props = {
    reportId: number;
    versionId: number;
    onCreate?: () => void;
};

type FormValues = {
    file?: ReportMediaObjectFieldsFragment;
    description?: string;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    file: yup.mixed<ReportMediaObjectFieldsFragment>().required(),
    description: yup.string(),
});

const initialValues: FormValues = {
    description: "",
};

export const CreateReportDeliverableFileForm: FC<Props> = ({ versionId, onCreate }) => {
    const [createDeliverableFile] = useCreateReportDeliverableFileMutation({});

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        createDeliverableFile({
            variables: {
                input: {
                    reportVersion: reportVersionGraphId(versionId),
                    reportMediaObject: values.file?.id ?? "",
                    description: values.description,
                },
            },
        })
            .then((response) =>
                response.data?.createReportDeliverableFile?.reportDeliverableFile ? onCreate?.() : undefined,
            )
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
            {({ handleSubmit, setFieldValue, setFieldTouched, values, isSubmitting, errors, touched, status }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <StatusAlert status={status as unknown} />

                    <Form.Group className="mb-3">
                        <Form.Label>File</Form.Label>
                        <div className={errors.file && "is-invalid"}>
                            <MediaObjects
                                initialValues={[]}
                                onChange={(values) => setFieldValue("file", values.length > 0 ? values[0] : undefined)}
                                versionId={versionId}
                                allowMultiple={false}
                            />
                        </div>
                        <Form.Control.Feedback type="invalid">{errors.file}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <RichTextEditor
                            value={values.description ?? ""}
                            onChange={(value) => setFieldValue("description", value)}
                            onBlur={() => setFieldTouched("description")}
                            isInvalid={touched.description && !!errors.description}
                        />
                        <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                    </Form.Group>

                    <Button type="submit" disabled={isSubmitting}>
                        Submit
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
